import { type FC, useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  dialogOverlayClasses,
} from '@/components/ui/dialog';
import { IngestionDamLayout } from '@/components/layouts/ingestion-dam-layout';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { IngestionLayout } from '@/components/layouts/ingestion-layout';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { useAsset } from '@/hooks/data/assets/useAsset';
import { Button } from '@/components/ui/button';
import { TaskState } from '@/types/uploads';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const UploadDialog: FC<{ refetchNode?: () => void }> = ({ refetchNode }) => {
  const queryClient = useQueryClient();
  const tCommon = useTranslations('common');
  const tDialogs = useTranslations('dialogs');
  const tUpload = useTranslations('upload');

  const {
    setUploadFolderPath,
    uploadModalOpen,
    setUploadModalOpen,
    clearAllFiles,
    getActiveUploadSession,
    setUploadStatus,
    setUploadState,
    createUploadSession,
    setUploadFolderId,
    setUploadField,
    startUpload,
    setSelectedAlbums,
  } = useUploadStore();
  const {
    session: { uploadState, filesData, uploadFolderPath },
    uploadSessionId,
  } = getActiveUploadSession();
  const { setReusableTreeSelectedFolder, reusableTreeSelectedFolder, setReusableTreeSelection } = useTreeStore();
  const { createAssetMutation } = useAsset();
  const openModal = useOpenModal();

  const closeUploadModal = useCallback(() => {
    openModal(ModalId.Confirm, {
      content: tUpload('closeSessionConfirmation'),
      dismissButtonLabel: tUpload('continueUpload'),
      dismissButtonDataCy: 'upload-dialog-confirm-dismiss',
      confirmButtonLabel: tCommon('close'),
      confirmButtonDataCy: 'upload-dialog-confirm-continue',
      onConfirm: () => {
        setUploadModalOpen(false);
        setUploadFolderPath(uploadSessionId, '');
        setReusableTreeSelectedFolder(undefined);
        setReusableTreeSelection(undefined);
        setSelectedAlbums(uploadSessionId, []);
        clearAllFiles(uploadSessionId);
      },
    });
  }, [
    openModal,
    tUpload,
    tCommon,
    setUploadModalOpen,
    uploadSessionId,
    setUploadFolderPath,
    clearAllFiles,
    setSelectedAlbums,
  ]);

  const onSubmit = async () => {
    const originalUploadSessionId = uploadSessionId;

    setUploadModalOpen(false);
    setUploadFolderId(originalUploadSessionId, reusableTreeSelectedFolder!);
    setUploadStatus(originalUploadSessionId, 'uploading');
    setUploadState(originalUploadSessionId, TaskState.InProgress);
    setUploadField(uploadSessionId, 'refetchNode', refetchNode);
    createUploadSession();
    setReusableTreeSelection(undefined);
    await startUpload(originalUploadSessionId, createAssetMutation, queryClient);
  };

  const uploadDisabled = useMemo(
    () =>
      uploadState === TaskState.InProgress ||
      filesData.length === 0 ||
      !reusableTreeSelectedFolder ||
      uploadFolderPath === '',
    [filesData.length, reusableTreeSelectedFolder, uploadState, uploadFolderPath],
  );

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    if (uploadModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = originalOverflow;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [uploadModalOpen]);

  return (
    <Dialog modal={false} open={uploadModalOpen} onOpenChange={closeUploadModal}>
      {uploadModalOpen && <div className={dialogOverlayClasses} />}
      <DialogContent
        id="upload-dialog"
        onInteractOutside={(event) => {
          // prevent closing when clicking outside
          event.preventDefault();
        }}
        className="flex h-[calc(100vh-3rem)] w-[calc(100vw-5rem)] max-w-[calc(100vw-5rem)] flex-col"
      >
        <DialogHeader>
          <DialogTitle>{tDialogs('uploadTitle')}</DialogTitle>
          <DialogDescription className="sr-only" />
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <IngestionDamLayout>
            <IngestionLayout />
          </IngestionDamLayout>
        </div>
        <DialogFooter>
          <Button autoFocus variant="secondary" data-cy="upload-dialog-clear-list-button" onClick={closeUploadModal}>
            {tCommon('cancel')}
          </Button>
          <Button
            disabled={uploadDisabled}
            type="submit"
            variant="info"
            data-cy="upload-dialog-start-upload-button"
            onClick={onSubmit}
            className={cn(uploadDisabled && 'bg-ui-base! text-text-gray-alternate!')}
          >
            {tUpload('upload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
